/**
 * CountUp
 *
 * @author jgreve <jgreve@vertic.com>
 * 
 */


class CountUp {
	constructor(args) {
		const defaults = {
			intervalSpeed: 10,
			countUpSpeed: 1000,
			startFromPercentage: 0,
		};

		this.options = Object.assign({}, defaults, args);
		this.options.countUpSpeed = this.options.countUpSpeed / this.options.intervalSpeed;

		// internal values
		this.countersLength = this.options.counters.length;
		this.triggered = false;
		this.done = false;

		// Setup requestAnimationFrame prefixing and fallback
		this.requestAnimationFrame = window.requestAnimationFrame ||
			window.webkitRequestAnimationFrame ||
			window.mozRequestAnimationFrame ||
			function animationFrame(callback) {
				window.setTimeout(callback, 1000 / 60);
			};
		this.requestAnimationFrame = this.requestAnimationFrame.bind(window);
	}

	countUpWhenInView() {
		if (this.countersLength === 0) {
			return false;
		}

		return new Promise((resolve) => {
			this._scrollHandler = this.scroll.bind(this, resolve);
			window.addEventListener('scroll', this._scrollHandler);
			this._scrollHandler(resolve);
		});
	}

	scroll(resolve) {
		this.latestKnownScrollY = window.scrollY || window.pageYOffset;

		if (!this.ticking) {
			this.requestAnimationFrame(this.checkScroll.bind(this, resolve));
		}

		this.ticking = true;
	}

	checkScroll(resolve) {
		this.ticking = false;

		const elem = this.options.counters[0];
		const elRect = elem.getBoundingClientRect();
		const winTop = window.scrollY || window.pageYOffset;
		const winBottom = winTop + window.innerHeight;
		const elTop = elRect.top + winTop;
		const elBottom = elTop + elRect.height;

		if (elBottom > winTop && elTop < winBottom && !this.triggered) {
			this.triggered = true;

			window.removeEventListener('scroll', this._scrollHandler);

			this.countUp().then(resolve);
		}
	}

	countUp() {
		if (this.countersLength === 0) {
			return false;
		}

		return new Promise((resolve) => {
			Array.from(this.options.counters).forEach((v, i) => {
                const input = v;
                const stringValue = input.dataset.val.toString();
                const numberStrings = stringValue.split(/[^0-9.,]+/g);
                const numbers = [];

				numberStrings.forEach((string) => {
					numbers.push(string ? this.prepareStrings(string) : '');
				});

				const regex = /[^0-9.,]+/g;
				let regexMatch = regex.exec(stringValue);
				const matches = [];

				do {
					matches.push(regexMatch);

					regexMatch = regex.exec(stringValue);
				} while (regexMatch);

				let counter = 1;
				const modifier = this.options.countUpSpeed;

				this.countUpLoop(() => {
					counter += 1;

					let textString = '';

					numbers.forEach((numberObj, numberIndex) => {
						const numberString = numberObj ? CountUp.countUpValue(numberObj, counter) : '';
						textString += numberString + (matches[numberIndex] ? matches[numberIndex][0] : '');
                    });
                    
                    input.innerText = textString;
                    

					// Countup has reached the end number
					if (counter >= modifier) {
						// All counters for the countup are complete
						if (i === this.countersLength - 1) {
							resolve();
							this.done = true;
						}

						return false;
					}

					return true;
				});
			});
		});
	}

	countUpLoop(render) {
		let running = true;
		let lastFrame = +new Date();

		const loop = (now) => {
			if (running !== false) {
				this.requestAnimationFrame(loop);
				const deltaT = now - lastFrame;

				// Do not render frame when deltaT is too high
				if (deltaT < 160) {
					running = render(now - lastFrame);
				}

				lastFrame = now;
			}
		};

		loop(lastFrame);
	}

	prepareStrings(stringValue) {
		const dividers = [];
		let hasDivider = false;
		let shouldPadNumber = false;

		stringValue.split('').forEach((char, index) => {
			if (char === '.' || char === ',') {
				hasDivider = true;
				let prevIndex = 0;

				if (dividers.length > 0) {
					prevIndex = dividers[dividers.length - 1].index + 1;
				}

				const prevChars = stringValue.substr(prevIndex, index - prevIndex);
				const nextChars = stringValue.substr(index + 1);

				dividers.push({
					char,
					index,
					prevChars,
					nextChars,
				});
			}
		});

		let valModifier = 0;
		const numberString = stringValue.replace(/,|\./g, '');
		let numberValue = parseFloat(numberString);
		const origNumberValue = numberValue;
		const modifier = this.options.countUpSpeed;
		const ratio = numberValue / modifier;

		if (this.options.startFromPercentage !== undefined) {
			valModifier = (numberValue / 100) * this.options.startFromPercentage;
			numberValue = (numberValue / 100) * (100 - this.options.startFromPercentage);
		}

		if (dividers.length > 0 && dividers[0].prevChars === '0') {
			shouldPadNumber = true;
		}

		return {
			hasDivider,
			dividers,
			origNumberValue,
			numberString,
			numberValue,
			valModifier,
			ratio,
			shouldPadNumber,
			stringLength: stringValue.length,
		};
	}

	static countUpValue(numberObj, counter) {
		let val = Math.min(Math.ceil((counter * numberObj.ratio) + numberObj.valModifier), numberObj.origNumberValue);

		// Existence check
		if (typeof Intl === 'object' && !numberObj.hasDivider) {
			// Format the number. This turns it into a string!
			// Using language code to format numbers correctly
			val = new Intl.NumberFormat(window.languageCode).format(val);
		}

		const newString = numberObj.hasDivider ? CountUp.createValueString(val, numberObj.dividers, numberObj) : val;

		return newString;
	}

	static createValueString(number, dividers, numberObj) {
		let string = numberObj.shouldPadNumber ? this.numberPad(number, numberObj.numberString.length) : number.toString();
		const dividersClone = dividers.slice(0);

		dividersClone.reverse().forEach((divider) => {
			if (dividersClone.length === 1 && divider.index === 0) {
				string = divider.char + string;
				return;
			}

			if (divider.nextChars.length >= string.length) {
				return;
			}

			const posFromEnd = string.length - divider.nextChars.length;
			string = string.substr(0, posFromEnd) + divider.char + string.substr(posFromEnd);
		});

		return string;
	}

	static numberPad(number, amount) {
		const n = `${number}`;
		return n.length >= amount ? n : new Array((amount - n.length) + 1).join('0') + n;
	}
}

export default CountUp;